import { lazy, Suspense } from 'react';

import { usePageSectionContext } from '@/components/page-sections/page-section-context';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import { hasRichTextContent } from '@/lib/rich-text';
import type { RichTextPersistMode } from './controlled-rich-text-editor';
import { RichTextPreview } from './rich-text-preview';

// Lazy load editor only when needed (reduces bundle size for public pages)
const InlineRichTextEditor = lazy(() =>
  import('./inline-rich-text-editor').then((mod) => ({ default: mod.InlineRichTextEditor })),
);

export interface RichTextEditorProps {
  value: string;
  fieldPosition: string;
  placeholder?: string;
  className?: string;
  headingsOnly?: boolean;
  onChange?: (html: string) => void;
  persistMode?: RichTextPersistMode;
}

/**
 * Rich text editor component that conditionally loads editor code.
 * - In editor mode: lazy-loads click-to-edit InlineRichTextEditor
 * - In preview mode: renders plain content (editor code never loaded)
 */
export function RichTextEditor({
  value,
  fieldPosition,
  placeholder = 'Start typing...',
  className,
  headingsOnly = false,
  onChange,
  persistMode,
}: RichTextEditorProps) {
  const isEditor = useIsPageEditor();
  const sectionContext = usePageSectionContext();
  const sectionId = sectionContext?.sectionId;
  const isStaticPreview =
    sectionContext?.renderMode != null && sectionContext.renderMode !== 'default';
  const canLoadEditor = Boolean(sectionId || onChange);
  const showEditor = isEditor && canLoadEditor && !isStaticPreview;

  if (!showEditor && !hasRichTextContent(value)) {
    return null;
  }

  if (showEditor) {
    return (
      <Suspense
        fallback={
          <RichTextPreview
            mode="document"
            value={value}
            className={className}
            placeholder={placeholder}
            headingsOnly={headingsOnly}
          />
        }
      >
        <InlineRichTextEditor
          value={value}
          sectionId={sectionId}
          fieldPosition={fieldPosition}
          placeholder={placeholder}
          className={className}
          headingsOnly={headingsOnly}
          onChange={onChange}
          persistMode={persistMode}
        />
      </Suspense>
    );
  }

  return <RichTextPreview mode="document" value={value} className={className} />;
}
